/**
 * Parse getTasks AWSJSON — backend returns stringified { Tasks: [...], nextToken }.
 */

export function parseTasksPayload(data) {
  if (data == null) return { list: [], nextToken: null };

  let parsed = data;
  while (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return { list: [], nextToken: null };
    }
  }

  if (Array.isArray(parsed)) {
    return { list: parsed, nextToken: null };
  }

  if (!parsed || typeof parsed !== "object") {
    return { list: [], nextToken: null };
  }

  const list =
    (Array.isArray(parsed.Tasks) && parsed.Tasks) ||
    (Array.isArray(parsed.TasksList) && parsed.TasksList) ||
    (Array.isArray(parsed.tasks) && parsed.tasks) ||
    (Array.isArray(parsed.items) && parsed.items) ||
    [];

  if (list.length === 0 && (parsed.taskId || parsed.id || parsed.missionId)) {
    return {
      list: [parsed],
      nextToken: parsed.nextToken ?? parsed.NextToken ?? null,
    };
  }

  const nextToken =
    parsed.nextToken ?? parsed.NextToken ?? parsed.next_token ?? null;

  return { list, nextToken };
}

export function extractTasksFromServiceResponse(res) {
  if (!res) return { list: [], nextToken: null };

  if (Array.isArray(res.data) && res.data.length > 0) {
    return { list: res.data, nextToken: res.nextToken ?? null };
  }

  if (res.raw != null) {
    const fromRaw = parseTasksPayload(res.raw);
    if (fromRaw.list.length > 0) return fromRaw;
  }

  if (res.data != null) {
    const fromData = parseTasksPayload(res.data);
    if (fromData.list.length > 0) return fromData;
  }

  if (Array.isArray(res.data)) {
    return { list: res.data, nextToken: res.nextToken ?? null };
  }

  return { list: [], nextToken: res.nextToken ?? null };
}

export function taskMissionId(task) {
  if (!task || typeof task !== "object") return null;

  const topLevel = task.missionId ?? task.MissionId;
  if (topLevel != null && String(topLevel).trim()) {
    return String(topLevel).trim();
  }

  const data = parseTaskDataRecord(task);
  const fromData = data?.missionId ?? data?.MissionId;
  if (fromData != null && String(fromData).trim()) {
    return String(fromData).trim();
  }

  const fromResource =
    task.resource?.find?.((r) => r.route === "mission-details")?.id ??
    task.resource?.[0]?.id;
  if (fromResource != null && String(fromResource).trim()) {
    return String(fromResource).trim();
  }

  return null;
}

function pickTaskDataEntryName(entry) {
  if (!entry || typeof entry !== "object") return "";
  return String(entry.name ?? entry.Name ?? entry.key ?? "").trim();
}

function pickTaskDataEntryValue(entry) {
  if (!entry || typeof entry !== "object") return undefined;
  if (entry.value !== undefined) return entry.value;
  if (entry.Value !== undefined) return entry.Value;
  return entry.val;
}

/** Normalize task.data from object, JSON string, or [{ name, value }] array. */
export function parseTaskDataRecord(task) {
  if (!task || typeof task !== "object") return null;

  let data = task.data;
  if (data == null) return null;

  while (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      return null;
    }
  }

  if (Array.isArray(data)) {
    const obj = {};
    for (const entry of data) {
      const name = pickTaskDataEntryName(entry);
      if (!name) continue;
      obj[name] = pickTaskDataEntryValue(entry);
    }
    return Object.keys(obj).length ? obj : null;
  }

  return typeof data === "object" && data !== null ? data : null;
}

export function isPilotInterestTask(task) {
  const type = String(task?.taskType ?? task?.type ?? "").toLowerCase();
  const category = String(
    task?.taskCategory ?? task?.category ?? "",
  ).toLowerCase();
  return type.includes("pilot") || category.includes("pilot");
}

export function parseInterestedPilotsList(raw) {
  if (raw == null) return [];

  let parsed = raw;
  while (typeof parsed === "string") {
    const trimmed = parsed.trim();
    if (!trimmed) return [];

    try {
      parsed = JSON.parse(trimmed);
      continue;
    } catch {
      if (/^[0-9a-f-]{36}$/i.test(trimmed)) return [trimmed];
      if (trimmed.includes(",")) {
        return trimmed
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean);
      }
      return [];
    }
  }

  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === "object") {
    const nested =
      parsed.pilots ??
      parsed.interestedPilots ??
      parsed.InterestedPilots ??
      parsed.items ??
      parsed.list;
    if (Array.isArray(nested)) return nested;
  }

  return [];
}

export function taskId(task) {
  return task?.taskId ?? task?.id ?? null;
}

function legIdFromLegsField(legsRaw) {
  if (legsRaw == null || !String(legsRaw).trim()) return null;

  let parsed = legsRaw;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }

  if (typeof parsed === "string" && parsed.trim()) {
    return parsed.trim();
  }

  if (Array.isArray(parsed)) {
    for (const entry of parsed) {
      if (entry == null) continue;
      if (typeof entry === "string" && entry.trim()) return entry.trim();
      if (typeof entry === "object") {
        const id = entry.legId ?? entry.leg_id ?? entry.LegId ?? entry.id;
        if (id != null && String(id).trim()) return String(id).trim();
      }
    }
    return null;
  }

  if (parsed && typeof parsed === "object") {
    const id = parsed.legId ?? parsed.leg_id ?? parsed.LegId ?? parsed.id;
    return id != null && String(id).trim() ? String(id).trim() : null;
  }

  return null;
}

/** Resolve legId from top-level task fields or task.data (incl. nested legs JSON). */
export function extractLegIdFromTask(task) {
  if (!task || typeof task !== "object") return null;

  const topLevel = task.legId ?? task.leg_id ?? task.LegId;
  if (topLevel != null && String(topLevel).trim()) {
    return String(topLevel).trim();
  }

  const data = parseTaskDataRecord(task);
  if (!data) return null;

  const direct = data.legId ?? data.leg_id ?? data.LegId;
  if (direct != null && String(direct).trim()) {
    return String(direct).trim();
  }

  return legIdFromLegsField(data.legs ?? data.Legs);
}

function parseLegsField(raw) {
  if (raw == null) return [];

  let parsed = raw;
  while (typeof parsed === "string") {
    const trimmed = parsed.trim();
    if (!trimmed) return [];
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return [];
    }
  }

  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === "object") return [parsed];
  return [];
}

/** Normalize a leg record from task or mission payloads. */
export function normalizeLegRecord(leg) {
  if (leg == null) return null;

  if (typeof leg === "string") {
    const legId = leg.trim();
    return legId
      ? {
          legId,
          startAirport: "",
          endAirport: "",
          pilotId: "",
          pilotName: "",
        }
      : null;
  }

  if (typeof leg !== "object") return null;

  const legId = String(
    leg.legId ?? leg.leg_id ?? leg.LegId ?? leg.id ?? "",
  ).trim();
  const startAirport = String(
    leg.startAirport ?? leg.departureAirportId ?? leg.start ?? "",
  ).trim();
  const endAirport = String(
    leg.endAirport ?? leg.arrivalAirportId ?? leg.end ?? "",
  ).trim();
  const pilotId = String(leg.pilotId ?? leg.pilot?.profileId ?? "").trim();
  const pilotName = String(leg.pilot?.name ?? leg.pilotName ?? "").trim();

  if (!legId && !startAirport && !endAirport && !pilotId && !pilotName) {
    return null;
  }

  return { legId, startAirport, endAirport, pilotId, pilotName };
}

export function normalizeMissionLegs(mission) {
  if (!mission || !Array.isArray(mission.legs)) return [];
  return mission.legs.map(normalizeLegRecord).filter(Boolean);
}

/** Legs embedded on the task before falling back to mission fetch. */
export function extractLegsFromTask(task) {
  if (!task || typeof task !== "object") return [];

  if (Array.isArray(task.legs)) {
    return task.legs.map(normalizeLegRecord).filter(Boolean);
  }

  if (task.mission && Array.isArray(task.mission.legs)) {
    return normalizeMissionLegs(task.mission);
  }

  const data = parseTaskDataRecord(task);
  if (data?.legs != null) {
    return parseLegsField(data.legs).map(normalizeLegRecord).filter(Boolean);
  }

  return [];
}

export function legsHaveRouteDetails(legs) {
  return (
    Array.isArray(legs) &&
    legs.some((leg) => leg?.startAirport || leg?.endAirport)
  );
}

/**
 * Payload fields for updateTask accept/reject — mirrors task.data shape from getTasks.
 */
export function buildTaskActionData(task, pilotId) {
  const missionId = taskMissionId(task);
  const legId = extractLegIdFromTask(task);
  const resolvedPilotId =
    pilotId != null && String(pilotId).trim() ? String(pilotId).trim() : null;

  const data = [];
  if (missionId) {
    data.push({ name: "missionId", value: String(missionId) });
  }
  if (legId) {
    data.push({ name: "legId", value: legId });
  }
  if (resolvedPilotId) {
    data.push({ name: "pilotId", value: resolvedPilotId });
  }
  return data;
}

/**
 * actionInput for updateTask — missionId, legId, and pilotId when available on the task.
 */
export function buildUpdateTaskInput(task, decision, actionConfig, pilotId) {
  const data = buildTaskActionData(task, pilotId);

  const defaultSubType = decision === "accept" ? "AcceptPilot" : "RejectPilot";

  if (actionConfig) {
    return {
      message: actionConfig.message ?? "Task",
      type: actionConfig.type ?? "TaskUpdate",
      subType: actionConfig.subType ?? defaultSubType,
      ...(data.length ? { data } : {}),
    };
  }

  return {
    message: "Task",
    type: "TaskUpdate",
    subType: defaultSubType,
    ...(data.length ? { data } : {}),
  };
}

export function canDecideTask(task) {
  const status = taskStatus(task).toLowerCase();
  if (
    [
      "accepted",
      "approved",
      "denied",
      "rejected",
      "completed",
      "closed",
    ].includes(status)
  ) {
    return false;
  }
  return Boolean(taskMissionId(task) && taskId(task));
}

export function taskResourceLabel(task) {
  const label = task?.resource?.[0]?.label;
  if (label && String(label).trim()) return String(label).trim();
  return taskMissionId(task) ? "View Mission" : null;
}

export function taskStatus(task) {
  return (task?.status ?? task?.taskStatus ?? task?.state ?? "")
    .toString()
    .trim();
}

export function formatTaskAge(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  const diffMs = Date.now() - parsed.getTime();
  if (diffMs < 0) return "Just now";

  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;

  const years = Math.floor(months / 12);
  return `${years} year${years === 1 ? "" : "s"} ago`;
}

function prettifyLabel(value) {
  if (!value) return "";

  const normalized = String(value).trim().toLowerCase().replace(/[_-]/g, " ");
  const knownLabels = {
    pilotinterested: "Pilot Interested",
    "pilot interested": "Pilot Interested",
  };
  if (knownLabels[normalized]) return knownLabels[normalized];

  return String(value)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export { prettifyLabel };

const DECIDED_TASK_STATUSES = new Set([
  "accepted",
  "approved",
  "denied",
  "rejected",
  "completed",
  "closed",
]);

const NEW_TASK_STATUSES = new Set(["new", "pending", "open", ""]);

export const TASK_LIST_TAB_KEYS = {
  ALL: "all",
  NEW: "new",
  COMPLETED: "completed",
};

export const TASK_LIST_TABS = [
  { key: TASK_LIST_TAB_KEYS.ALL, label: "All" },
  { key: TASK_LIST_TAB_KEYS.NEW, label: "New" },
  { key: TASK_LIST_TAB_KEYS.COMPLETED, label: "Completed" },
];

export function normalizeTaskStatusKey(status) {
  return String(status ?? "").trim().toLowerCase();
}

export function isTaskStatusNew(task) {
  return NEW_TASK_STATUSES.has(normalizeTaskStatusKey(taskStatus(task)));
}

export function isTaskStatusCompleted(task) {
  return DECIDED_TASK_STATUSES.has(normalizeTaskStatusKey(taskStatus(task)));
}

export function taskMatchesStatusTab(task, tabKey) {
  if (!tabKey || tabKey === TASK_LIST_TAB_KEYS.ALL) return true;
  if (tabKey === TASK_LIST_TAB_KEYS.NEW) return isTaskStatusNew(task);
  if (tabKey === TASK_LIST_TAB_KEYS.COMPLETED) return isTaskStatusCompleted(task);
  return true;
}

export function shouldShowTaskStatusBadge(status) {
  return DECIDED_TASK_STATUSES.has(normalizeTaskStatusKey(status));
}

const HIDDEN_STATUS_PILLS = new Set(["new", "pending", "open"]);

/** Primary + secondary category pills (blue / orange). */
export function getTaskCategoryPills(task) {
  const pills = [];
  const category = (task?.taskCategory ?? task?.category ?? "").trim();
  const type = (task?.taskType ?? task?.type ?? "").trim();
  const status = taskStatus(task);

  if (category) {
    pills.push({ label: prettifyLabel(category), tone: "primary" });
  }
  if (type && type.toLowerCase() !== category.toLowerCase()) {
    pills.push({ label: prettifyLabel(type), tone: "secondary" });
  } else if (
    status &&
    !HIDDEN_STATUS_PILLS.has(status.toLowerCase()) &&
    status.toLowerCase() !== category.toLowerCase()
  ) {
    pills.push({ label: prettifyLabel(status), tone: "secondary" });
  }

  if (
    pills.length === 0 &&
    status &&
    !HIDDEN_STATUS_PILLS.has(status.toLowerCase())
  ) {
    pills.push({ label: prettifyLabel(status), tone: "primary" });
  }

  return pills;
}

function stripTrailingColon(value) {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .replace(/:\s*$/, "")
    .trim();
}

function stripCategorySuffix(desc, category) {
  if (!desc || !category) return stripTrailingColon(desc);

  const escaped = category.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return stripTrailingColon(
    desc.replace(new RegExp(`:\\s*${escaped}\\s*$`, "i"), ""),
  );
}

export function buildTaskDescription(task) {
  const category = (task?.taskCategory ?? "").trim();
  const template = (task?.taskDescriptionTemplate ?? "").trim();
  let desc = (task?.taskDescription ?? "").trim();

  if (template) {
    desc = template
      .replace(/<missionCategory>/gi, "")
      .replace(/<[^>]+>/g, "")
      .trim();
  }

  desc = stripCategorySuffix(desc, category);

  if (!desc) {
    return taskResourceLabel(task) || "Task notification";
  }

  return desc;
}

export function getTaskCreatedTime(task) {
  return task?.createdAt ?? task?.dueDate ?? null;
}

export function sortTasksByNewest(tasks) {
  return [...tasks].sort((a, b) => {
    const ta = new Date(getTaskCreatedTime(a) || 0).getTime();
    const tb = new Date(getTaskCreatedTime(b) || 0).getTime();
    return tb - ta;
  });
}
